import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, BookOpen, ChevronLeft, ChevronRight, Sparkles, Loader2, Type, ChevronDown, X, Moon, Sun } from 'lucide-react'
import { getBookById, BIBLE_BOOKS } from '../config/bibleConfig'
import { getChapter } from '../services/bibleApi'
import ParticleBackground from '../components/ParticleBackground'

const BibleReader: React.FC = () => {
  const { bookId, chapter } = useParams<{ bookId: string; chapter?: string }>()
  const navigate = useNavigate()
  const [selectedChapter, setSelectedChapter] = useState<number>(chapter ? parseInt(chapter, 10) : 1)
  const [chapterText, setChapterText] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showChapterList, setShowChapterList] = useState(false)
  const [showBookSelector, setShowBookSelector] = useState(false)
  const [showFontSizeMenu, setShowFontSizeMenu] = useState(false)
  const [fontSize, setFontSize] = useState<number>(() => {
    const saved = localStorage.getItem('bible-font-size')
    return saved ? parseInt(saved, 10) : 18
  })
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('bible-theme')
    return saved ? saved === 'dark' : false
  })

  const book = bookId ? getBookById(bookId) : null

  // Group books by testament
  const oldTestamentBooks = BIBLE_BOOKS.filter(b => b.testament === 'old')
  const newTestamentBooks = BIBLE_BOOKS.filter(b => b.testament === 'new')

  // Save font size to localStorage
  useEffect(() => {
    localStorage.setItem('bible-font-size', fontSize.toString())
  }, [fontSize])

  // Save theme to localStorage
  useEffect(() => {
    localStorage.setItem('bible-theme', isDarkMode ? 'dark' : 'light')
  }, [isDarkMode])

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev)
  }

  const handleBookSelect = (selectedBookId: string) => {
    navigate(`/book/${selectedBookId}`)
    setShowBookSelector(false)
  }

  useEffect(() => {
    if (chapter) {
      const chapterNum = parseInt(chapter, 10)
      if (!isNaN(chapterNum)) {
        setSelectedChapter(chapterNum)
      }
    }
  }, [chapter])

  const loadChapter = useCallback(async (bookId: string, chapterNum: number) => {
    setLoading(true)
    setError(null)
    
    try {
      const text = await getChapter(bookId, chapterNum)
      if (text) {
        setChapterText(text)
      } else {
        setError('Could not load chapter. Please check your ESV API key.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chapter')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (book && selectedChapter) {
      loadChapter(book.id, selectedChapter)
    }
  }, [book, selectedChapter, loadChapter])

  const handleChapterChange = (newChapter: number) => {
    if (newChapter >= 1 && newChapter <= (book?.chapters || 1)) {
      setSelectedChapter(newChapter)
      navigate(`/book/${bookId}/${newChapter}`, { replace: true })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePreviousChapter = () => {
    if (selectedChapter > 1) {
      handleChapterChange(selectedChapter - 1)
    }
  }

  const handleNextChapter = () => {
    if (book && selectedChapter < book.chapters) {
      handleChapterChange(selectedChapter + 1)
    }
  }

  const handleGenerateSlides = () => {
    if (book) {
      navigate(`/ai-generator?reference=${encodeURIComponent(`${book.name} ${selectedChapter}`)}`)
    }
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl mb-4">Book not found</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-bible-gold text-slate-900 rounded-lg font-semibold hover:bg-yellow-500 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative">
      <ParticleBackground />
      
      <div className="relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700"
        >
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-1.5 text-bible-gold hover:text-yellow-300 transition-colors"
              >
                <ArrowLeft size={18} />
                <span className="hidden sm:inline text-sm">Back</span>
              </button>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={handleGenerateSlides}
                  className="flex items-center gap-1 px-2.5 py-1 bg-bible-gold text-slate-900 rounded-lg hover:bg-yellow-500 transition-colors text-xs sm:text-sm font-medium"
                  title="Generate Slides"
                >
                  <Sparkles size={14} />
                  <span className="hidden sm:inline">Generate</span>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => setShowBookSelector(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm flex-1 max-w-[200px] sm:max-w-none"
              >
                <BookOpen size={16} />
                <span className="truncate">{book.name}</span>
                <ChevronDown size={14} />
              </button>
              
              <div className="flex items-center gap-2">
                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-1 px-2.5 py-1 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm"
                  title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                </button>

                {/* Font Size Selector */}
                <div className="relative">
                  <button
                    onClick={() => setShowFontSizeMenu(!showFontSizeMenu)}
                    className="flex items-center gap-1 px-2.5 py-1 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm"
                    title="Font Size"
                  >
                    <Type size={16} />
                    <span className="hidden sm:inline">{fontSize}px</span>
                  </button>
                  
                  {showFontSizeMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowFontSizeMenu(false)}
                      />
                      <div className="absolute right-0 top-full mt-2 bg-slate-800 rounded-lg border border-slate-700 p-3 z-50 min-w-[180px] max-w-[200px] shadow-xl">
                        <div className="text-xs text-gray-400 mb-2 font-medium">Font Size</div>
                        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                          {[14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40].map(size => (
                            <button
                              key={size}
                              onClick={() => {
                                setFontSize(size)
                                setShowFontSizeMenu(false)
                              }}
                              className={`w-full px-3 py-2 rounded text-sm text-left transition-colors ${
                                fontSize === size
                                  ? 'bg-bible-gold text-slate-900 font-semibold'
                                  : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                              }`}
                            >
                              {size}px
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Chapter Selector */}
                <button
                  onClick={() => setShowChapterList(!showChapterList)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm"
                >
                  <span className="hidden sm:inline">Ch. </span>
                  <span>{selectedChapter}</span>
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Book Selector Modal */}
        <AnimatePresence>
          {showBookSelector && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-30"
                onClick={() => setShowBookSelector(false)}
              />
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                className="fixed top-16 sm:top-20 left-2 right-2 sm:left-auto sm:right-4 sm:w-96 bg-slate-800 rounded-lg border border-slate-700 z-40 max-h-[85vh] overflow-hidden flex flex-col"
              >
                <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-bible-gold">Select Book</h3>
                  <button
                    onClick={() => setShowBookSelector(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="overflow-y-auto flex-1">
                  {/* Old Testament */}
                  <div className="p-4">
                    <h4 className="text-sm font-semibold text-bible-gold mb-3 uppercase tracking-wide">Old Testament</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
                      {oldTestamentBooks.map((b) => (
                        <button
                          key={b.id}
                          onClick={() => handleBookSelect(b.id)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                            bookId === b.id
                              ? 'bg-bible-gold text-slate-900'
                              : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                          }`}
                        >
                          {b.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* New Testament */}
                  <div className="p-4 pt-0 border-t border-slate-700">
                    <h4 className="text-sm font-semibold text-bible-gold mb-3 uppercase tracking-wide">New Testament</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {newTestamentBooks.map((b) => (
                        <button
                          key={b.id}
                          onClick={() => handleBookSelect(b.id)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                            bookId === b.id
                              ? 'bg-bible-gold text-slate-900'
                              : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                          }`}
                        >
                          {b.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Chapter List Modal */}
        <AnimatePresence>
          {showChapterList && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-30"
                onClick={() => setShowChapterList(false)}
              />
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                className="fixed top-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-slate-800 rounded-lg border border-slate-700 z-40 max-h-[70vh] overflow-y-auto"
              >
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-bible-gold mb-3">Select Chapter</h3>
                  <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
                    {Array.from({ length: book.chapters }, (_, i) => i + 1).map((chap) => (
                      <button
                        key={chap}
                        onClick={() => {
                          handleChapterChange(chap)
                          setShowChapterList(false)
                        }}
                        className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                          chap === selectedChapter
                            ? 'bg-bible-gold text-slate-900'
                            : 'bg-slate-700 text-white hover:bg-slate-600'
                        }`}
                      >
                        {chap}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Chapter Navigation */}
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePreviousChapter}
              disabled={selectedChapter === 1}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={20} />
              <span className="hidden sm:inline">Previous</span>
            </button>

            <div className="text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-bible-gold">
                Chapter {selectedChapter}
              </h2>
              <p className="text-sm text-gray-400">
                {selectedChapter} of {book.chapters}
              </p>
            </div>

            <button
              onClick={handleNextChapter}
              disabled={selectedChapter === book.chapters}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Chapter Content */}
        <div className="container mx-auto px-4 pb-20 sm:pb-24 md:pb-16">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-bible-gold" size={48} />
            </div>
          ) : error ? (
            <div className="bg-red-900/50 border border-red-700 rounded-lg p-6 text-center">
              <p className="text-red-200 mb-4">{error}</p>
              <p className="text-sm text-red-300">
                Make sure you have set your ESV API key in the .env file
              </p>
            </div>
          ) : chapterText ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`backdrop-blur-sm rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 border shadow-lg max-w-4xl mx-auto overflow-visible transition-colors duration-300 ${
                isDarkMode
                  ? 'bg-slate-800 border-slate-700'
                  : 'bg-[#FAF9F6] border-amber-100'
              }`}
            >
              {/* Bible Text Container */}
              <div 
                className={`bible-reading-text ${isDarkMode ? 'bible-reading-text-dark' : 'bible-reading-text-light'}`}
                style={{ 
                  fontSize: `${fontSize}px`,
                  maxWidth: '100%',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                {chapterText.split(/\n\n+/).map((paragraph, idx) => {
                  // Remove verse numbers from the text and display cleanly
                  const cleanParagraph = paragraph
                    .replace(/^\d+\s+/gm, '') // Remove verse numbers at start of lines
                    .replace(/\s+/g, ' ') // Normalize whitespace
                    .trim()
                  
                  if (!cleanParagraph) return null
                  
                  return (
                    <p 
                      key={idx} 
                      className="bible-paragraph"
                    >
                      {cleanParagraph}
                    </p>
                  )
                })}
              </div>

              {/* Verse Reference */}
              <div className={`mt-8 sm:mt-10 pt-6 sm:pt-8 pb-4 sm:pb-6 border-t text-center ${
                isDarkMode ? 'border-slate-600' : 'border-amber-200'
              }`}>
                <p className={`font-medium text-xs sm:text-sm md:text-base tracking-wide leading-relaxed ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {book.name} {selectedChapter} (ESV)
                </p>
              </div>
            </motion.div>
          ) : null}
        </div>

        {/* Bottom Navigation (Mobile) */}
        <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700 p-4 sm:hidden z-20">
          <div className="flex items-center justify-between">
            <button
              onClick={handlePreviousChapter}
              disabled={selectedChapter === 1}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={20} />
              <span>Prev</span>
            </button>

            <button
              onClick={() => setShowChapterList(true)}
              className="px-4 py-2 bg-bible-gold text-slate-900 rounded-lg font-semibold"
            >
              {selectedChapter}
            </button>

            <button
              onClick={handleNextChapter}
              disabled={selectedChapter === book.chapters}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next</span>
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BibleReader

